import React, {FC, useCallback, useContext, useMemo, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {Box, Button, CircularProgress} from "@material-ui/core";
import {groupBy, round} from "lodash";

import Alert from "view/components/Alert";
import {
  BankTransferBillingDetails,
  goToPayment,
  resetCartStepper,
  selectBillingDetails,
} from "store/exhibitors/cartStepper/slice";
import {useDispatch, useSelector} from "react-redux";
import {useForm} from "react-hook-form";
import Form, {BasicSelect} from "view/components/Form";
import stripeService from "api/stripe";
import {BankTransfer, PaymentMethodType} from "ts/api/paymentMethod";
import {OrderInvoices} from "ts/api/order";
import {useBeforeUnload} from "hooks";
import {removeItem} from "tools/localStorage";
import {ErrorType} from "ts/api/error";
import orderService from "api/order";
import FlexBox from "view/components/Layout/FlexBox";
import BackdropLoader from "view/components/BackdropLoader";
import {LocalMapContext} from "view/context/LocalMapContext";
import getInvoices from "tools/order/getInvoices";
import useRedirectToOrderPage from "view/apps/Exhibitor/pages/Cart/components/Payment/components/useRedirectToOrderPage";
import {
  IExhibitorPair,
  IExhibitorUser,
  IOrganiser,
  IShow,
} from "@evennt-tech/api-v2-core";
import {generateTestId} from "tools/cypress/generateTestId";
import {
  INVOICE_DESCRIPTION_LIMIT,
  MIN_AMOUNT_BY_CURRENCY,
} from "view/apps/Exhibitor/pages/Cart/components/Payment/shared";
import {
  calculateFullProductPrice,
  calculateSupplierProductsFeesAmount,
} from "tools/product/productCalculations";
import getTaxAmount from "tools/common/getTaxAmount";
import {
  calculateSupplierPaymentFeeAmount,
  getDiscountAmount,
} from "view/apps/Exhibitor/pages/Cart/helpers";
import {FullProduct} from "ts/api/product";
import separateProductsWithApproval from "tools/product/separateProductsWithApproval";
import {
  useIsShowDiscountCodesInvalid,
  useShowDiscountCodesActions,
} from "store/discount-code/discount-code-store";
import {useQuickOrderId} from "hooks/show/order";
import {getUniqueProductCategoryIdsBySupplier} from "tools/product/getUniqueProductCategoryIdsBySupplier";
import {getTruncatedString} from "tools/common/getTruncatedString";
import {format} from "date-fns";
import {getReadableString} from "constraints/general";
import ConfirmBankTransferModal from "./components/ConfirmBankTransferModal";
import {useStyles} from "../../../style";
import euCountries from "./helpers/euCountries";
import getAlertInfo from "./helpers/getAlertInfo";
import shouldMakeBankTransferPaymentInBrowser from "./helpers/shouldMakeBankTransferPaymentInBrowser";
import getCountryByCurrency from "../../../../helpers/getCountryByCurrency";
import {CartFlow, CartFlowType} from "../../types";
import incrementProductsPurchasedQuantity from "../Card/helpers/incrementProductsPurchasedQuantity";
import {PlaceOrderCallback} from "../../../types";
import ProductBlocks from "./components/ProductBlocks";

type Props = CartFlow & {
  show: IShow;
  totalAmount: number;
  organiser: IOrganiser;
  exhibitorPair: IExhibitorPair;
  exhibitorUser: IExhibitorUser;
  orderDescription: string;
  transferredPaymentDescription: string;
  onPlaceOrder: PlaceOrderCallback;
};

type PaymentMethodFormValues = {
  country: string;
  poNumbers: {[k: string]: string};
};

const PaymentMethod: FC<Props> = ({
  type: cartFlowType,
  products,
  show,
  organiser,
  exhibitorPair,
  exhibitorUser,

  totalAmount,
  orderDescription: description,
  transferredPaymentDescription,
  onPlaceOrder,
  orderId,
}) => {
  const quickOrderId = useQuickOrderId();
  const localMap = useContext(LocalMapContext);
  const {t} = useTranslate();
  const billingDetails = useSelector(selectBillingDetails);

  const classes = useStyles();
  const dispatch = useDispatch();
  const redirectToOrderPage = useRedirectToOrderPage(show.id, exhibitorPair.id);
  const isDiscountCodesInvalid = useIsShowDiscountCodesInvalid(show.id);
  const {
    validate: validateDiscountCodes,
    reset: resetAppliedDiscountCodes,
  } = useShowDiscountCodesActions();

  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [
    showConfirmBankTransferModal,
    setShowConfirmBankTransferModal,
  ] = useState(false);
  const form = useForm<PaymentMethodFormValues>({mode: "all"});

  const {watch} = form;
  const formCountry = watch("country");
  const poNumbers = watch("poNumbers");

  const billingMetadata: BankTransferBillingDetails = {
    vat: billingDetails.vat,
    address: billingDetails.address,
    contact: billingDetails.contact,
    contactEmail: billingDetails.contactEmail,
    contactPhoneNumber: billingDetails.contactPhoneNumber,
    postCode: billingDetails.postCode,
  };

  useBeforeUnload(isSubmitting);

  const MIN_BANK_TRANSFER_INVOICE_AMOUNT = useMemo(() => {
    return MIN_AMOUNT_BY_CURRENCY[show.currency];
  }, [show]);
  const categoryIdsBySupplier = useMemo(
    () => getUniqueProductCategoryIdsBySupplier(products),
    [products]
  );

  const allInvoicesMoreThanMinAmount = useMemo(() => {
    const {
      productsWithApproval,
      productsWithoutApproval,
    } = separateProductsWithApproval(products);
    const productsWithApprovalGroupedBySupplier = groupBy(
      productsWithApproval,
      "owner.id"
    );
    const productsWithoutApprovalGroupedBySupplier = groupBy(
      productsWithoutApproval,
      "owner.id"
    );

    const checkByProductsBySupplier = (products: FullProduct[]): boolean => {
      const supplierPaymentFeeAmount = calculateSupplierPaymentFeeAmount(
        products
      );

      const feesAmount = calculateSupplierProductsFeesAmount({
        products,
        includeFees: true,
      });
      const productsWithoutDiscountAmount = products.reduce(
        (sum, product) =>
          sum +
          calculateFullProductPrice({
            product,
            includeFees: true,
          }),
        0
      );
      const discountAmount = round(getDiscountAmount(products, true), 2);
      const productsAmount = Math.max(
        0,
        productsWithoutDiscountAmount - discountAmount
      );
      const subtotal = feesAmount + supplierPaymentFeeAmount + productsAmount;
      const taxAmount = getTaxAmount(subtotal, show.salesTaxPercentage);

      const totalSupplierPrice = subtotal + taxAmount;
      if (discountAmount >= productsWithoutDiscountAmount) {
        return true;
      }
      return totalSupplierPrice >= MIN_BANK_TRANSFER_INVOICE_AMOUNT;
    };

    const allProductsBySuppliers: FullProduct[][] = [
      ...Object.values(productsWithApprovalGroupedBySupplier),
      ...Object.values(productsWithoutApprovalGroupedBySupplier),
    ];

    return allProductsBySuppliers.every(checkByProductsBySupplier, false);
  }, [products, show]);

  const disableSubmit = useMemo(
    // () => (show.currency === "eur" && !formCountry) || isSubmitting,
    () => isSubmitting || !allInvoicesMoreThanMinAmount,
    [isSubmitting, allInvoicesMoreThanMinAmount]
  );

  if (cartFlowType === CartFlowType.local && !products?.length) {
    return (
      <Box className={classes.loaderContainer}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const clearCart = () => {
    removeItem("products");
  };

  const openConfirmBankTransferModal = () =>
    setShowConfirmBankTransferModal(true);
  const closeConfirmBankTransfewrModal = () =>
    setShowConfirmBankTransferModal(false);

  const getCountry = () => {
    if (show.currency === "eur") {
      if (formCountry) {
        return formCountry;
      }

      return "NL";
    }
    return getCountryByCurrency(show.currency);
  };

  const handleMakePayment = async (
    orderId: string,
    invoices: OrderInvoices
  ) => {
    const country = getCountry();

    const customerId = await stripeService.getCustomerId({
      email: exhibitorUser?.user?.email as string,
      currency: show.currency,
    });
    if (!customerId) {
      throw new Error("Customer id doesn't exist");
    }

    const truncatedDescription =
      description.length > INVOICE_DESCRIPTION_LIMIT
        ? `${description.slice(0, INVOICE_DESCRIPTION_LIMIT - 3)}...`
        : description;

    await Promise.all(
      Object.values(invoices).map(async (invoice) =>
        stripeService.makePayment<BankTransfer>({
          invoiceId: invoice.id,
          amount: Math.max(
            invoice.totalAmount,
            MIN_BANK_TRANSFER_INVOICE_AMOUNT
          ),
          description: truncatedDescription,
          currency: show.currency,
          customerId,
          country,
          orderId,
          showId: show.id,
          type: "bank_transfer" as PaymentMethodType.bankTransfer,
          billingDetails: {
            ...billingDetails,
            contactPhoneNumber: billingDetails.contactPhoneNumber?.toString(),
          },
          showName: getTruncatedString(show.name, 50),
          showStartDate: format(
            new Date(show.liveStart),
            `${getReadableString(show)}, HH:mm`
          ),
        })
      )
    );
  };

  const handleSubmit = useCallback(async () => {
    try {
      const isDiscountCodeValid = await validateDiscountCodes(
        show.id,
        exhibitorPair.standId,
        categoryIdsBySupplier
      );
      if (!isDiscountCodeValid) {
        return setShowConfirmBankTransferModal(false);
      }
      setIsSubmitting(true);
      if (error) {
        setError("");
      }
      if (cartFlowType === CartFlowType.local) {
        const placeOrderResult = await onPlaceOrder({
          poNumbers,
          billingMetadata,
        });
        if (!placeOrderResult) {
          throw new Error("Order(s) didn't create");
        }
        const {
          orderWithoutApproval,
          ordersWithApproval,
          groupId,
          invoicesWithoutApproval,
        } = placeOrderResult;
        const idWithoutApproval = orderWithoutApproval?.id;
        const idsWithApproval = ordersWithApproval?.map(({id}) => id);
        const orderId = idWithoutApproval || idsWithApproval?.[0];

        if (!orderId) {
          throw new Error("Order(s) didn't create");
        }

        // Standard orders are created and finalized by the server-side
        // onCreate flow. Quick orders do not use that trigger path.
        if (
          shouldMakeBankTransferPaymentInBrowser({
            cartFlowType,
            quickOrderId,
          }) &&
          idWithoutApproval &&
          invoicesWithoutApproval
        ) {
          await handleMakePayment(idWithoutApproval, invoicesWithoutApproval);
        }
        // NOTE: orders requiring Supplier approval must not get a real
        // invoice at checkout — it's generated once the Supplier approves
        // (see ffair-firebase triggers/orders/onUpdate.ts).
        if (!quickOrderId) {
          await incrementProductsPurchasedQuantity(show.id, products);
        }
        await orderService.markOrderProductImagesUsed(products);
        redirectToOrderPage(orderId, groupId);
      } else if (cartFlowType === CartFlowType.created && orderId) {
        // NOTE: wtf this flow is nonsense. Need to discuss
        const productsBySupplier = Object.values(groupBy(products, "owner.id"));
        const invoices = await getInvoices({
          organiser,
          show,
          poNumbers,
          transferredPaymentDescription,
          orderId,
          fullProducts: productsBySupplier,
          billingMetadata,
        });
        const {totalAmount} = Object.values(invoices)[0];
        await handleMakePayment(orderId, invoices);
        await orderService.proceedToPayment(show.id, orderId, {
          paymentType: PaymentMethodType.bankTransfer,
          invoices,
          totalAmount,
        });
        await incrementProductsPurchasedQuantity(show.id, products);
        redirectToOrderPage(orderId);
      }
      resetAppliedDiscountCodes(show.id);
      dispatch(resetCartStepper());
      clearCart();
      setIsSubmitting(false);
    } catch (error) {
      const typedError = error as ErrorType;
      setIsSubmitting(false);
      if (typedError.message) {
        setError(typedError.message);
      }
      console.error(typedError);
    }
  }, [
    setIsSubmitting,
    cartFlowType,
    onPlaceOrder,
    orderId,
    organiser,
    show,
    exhibitorPair,
    exhibitorUser,
    transferredPaymentDescription,
    description,
    formCountry,
    poNumbers,
    MIN_BANK_TRANSFER_INVOICE_AMOUNT,
  ]);

  return (
    <>
      <BackdropLoader isLoading={isSubmitting} />

      <Form
        form={form}
        style={{padding: 0}}
        className={classes.form}
        onSubmit={() => null}
      >
        <Box my={2}>
          <Alert icon={false} severity="info">
            {getAlertInfo(exhibitorUser?.user?.email as string, localMap)}
          </Alert>
        </Box>
        {show.currency === "eur" && (
          <BasicSelect
            label={t("exhibitor.cart.bank_transfer.select_country")}
            name="country"
            required={false}
            options={euCountries.map(({code, name}) => ({
              value: code,
              label: name,
            }))}
          />
        )}
        <ProductBlocks
          products={products}
          show={show}
          cartFlowType={cartFlowType}
        />
        {!!error && (
          <Alert severity="error" className={classes.alert}>
            {error}
          </Alert>
        )}
        <FlexBox
          mt={3}
          justifyContent={
            cartFlowType === CartFlowType.local ? "space-between" : "flex-end"
          }
        >
          {cartFlowType === CartFlowType.local && (
            <Button
              {...generateTestId("exhibitor-cart-change-billing-details-btn")}
              className={classes.buttonBackToCart}
              color="primary"
              onClick={() => dispatch(goToPayment())}
            >
              <T keyName="exhibitor.cart.payment.change_billing_details" />
            </Button>
          )}
          <Button
            {...generateTestId("exhibitor-cart-place-order-btn")}
            onClick={openConfirmBankTransferModal}
            disabled={disableSubmit || isDiscountCodesInvalid}
            classes={{text: classes.buttonText}}
            color="primary"
            variant="contained"
          >
            <T keyName="exhibitor.cart.bank_transfer.place_order" />
          </Button>
        </FlexBox>
      </Form>
      {showConfirmBankTransferModal && (
        <ConfirmBankTransferModal
          onClose={closeConfirmBankTransfewrModal}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default PaymentMethod;
